import React, { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useInviteMemberMutation } from "@/store/rest_api";
import { toast } from "sonner";
import { Loader2, Mail } from "lucide-react";
import { MembershipRole } from "@/enum/MembershipRole";

const inviteSchema = z.object({
  email: z.string().min(1, "Email is required").email("Please enter a valid email address"),
  role: z.string().min(1, "Role is required"),
});

type InviteFormValues = z.infer<typeof inviteSchema>;

interface InviteMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canInviteAdmins: boolean;
}

export default function InviteMemberDialog({ open, onOpenChange, canInviteAdmins }: InviteMemberDialogProps) {
  const [inviteMember, { isLoading }] = useInviteMemberMutation();

  const form = useForm<InviteFormValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      email: "",
      role: MembershipRole.MEMBER,
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({ email: "", role: MembershipRole.MEMBER });
    }
  }, [open]);

  const onSubmit = async (values: InviteFormValues) => {
    try {
      await inviteMember({
        user: { email: values.email },
        role: values.role as MembershipRole,
      }).unwrap();

      toast.success(`Invitation sent to ${values.email}`);
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.detail || "Failed to send invitation");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Invite Team Member</DialogTitle>
          <DialogDescription>
            Send an invitation to join your organization. They'll receive an email with a link to accept.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 py-4">
            <FormField control={form.control} name="email" render={({ field }) => (
              <FormItem>
                <FormLabel>Email Address *</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input type="email" placeholder="colleague@example.com" className="pl-9" {...field} />
                  </div>
                </FormControl>
                <p className="text-xs text-muted-foreground">An invitation email will be sent to this address</p>
                <FormMessage />
              </FormItem>
            )} />

            <FormField control={form.control} name="role" render={({ field }) => (
              <FormItem>
                <FormLabel>Role *</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                  <SelectContent>
                    <SelectItem value={MembershipRole.MEMBER}>
                      <div className="flex flex-col items-start">
                        <span className="font-medium">Member</span>
                        <span className="text-xs text-muted-foreground">Can make calls and view contacts</span>
                      </div>
                    </SelectItem>
                    {canInviteAdmins && (
                      <SelectItem value={MembershipRole.ADMIN}>
                        <div className="flex flex-col items-start">
                          <span className="font-medium">Admin</span>
                          <span className="text-xs text-muted-foreground">Can manage team members and settings</span>
                        </div>
                      </SelectItem>
                    )}
                    <SelectItem value={MembershipRole.AI_SUPERVISOR}>
                      <div className="flex flex-col items-start">
                        <span className="font-medium">AI supervisor</span>
                        <span className="text-xs text-muted-foreground">A member who can also take over live AI calls</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                Send Invitation
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
